import { FC, memo, useMemo, useState } from 'react'
import FundraisingSelect from '../FundraisingSelect'
import { Flex } from '@mantine/core'
import { FundraisingAmountProps } from '../FundraisingAmount'
import { Box, Button, ButtonRootVariable, Input } from '@gate/gui'
import { useDebounceFn, useRequest } from 'ahooks'
import { getFundraisingInvestorSelect } from 'api/fundraising'
import { observer } from 'mobx-react-lite'
import { useStore } from '@/store/index'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'

const FundraisingInvestors: FC<FundraisingAmountProps> = ({
  title,
  fundraisingSelect,
  setFundraisingSelect,
  onFundraisingSelectChange,
  justify = 'flex-end',
}) => {
  const { t } = useTranslation()
  const { locale = 'en' } = useRouter()
  const { setInvestor, fundraising } = useStore().baseStore
  const [search, setSarch] = useState<string>('')

  const { loading, runAsync } = useRequest(getFundraisingInvestorSelect, {
    manual: true,
  })

  const { run: debounce } = useDebounceFn(
    value => {
      runAsync({
        page: 1,
        pageSize: 20,
        lang: locale,
        name: value,
      }).then(res => {
        setInvestor(res)
      })
    },
    { wait: 500 }
  )

  const isMore = useMemo(() => {
    if (!fundraising.investorSelect?.list?.length) {
      return false
    }
    if ((fundraising?.investorSelect?.totalCount ?? 0) <= fundraising.investorSelect?.list?.length) {
      return false
    }
    return true
  }, [fundraising.investorSelect?.list?.length, fundraising.investorSelect?.totalCount])

  return (
    <Flex w="100%" justify={justify}>
      <FundraisingSelect
        searchNode={
          <Box px={8} pb={8}>
            <Input
              w={'100%'}
              icon={
                <svg className="iconpark-icon size-14">
                  <use href="#v31-16px-line-sousuo"></use>
                </svg>
              }
              placeholder={t('P.Search') + ''}
              value={search}
              onChange={(e: any) => {
                setSarch(e.target.value)
                debounce(e.target.value)
              }}
              onClear={() => {
                setSarch('')
                runAsync({
                  page: 1,
                  pageSize: 20,
                  lang: locale,
                }).then(res => {
                  setInvestor(res)
                })
              }}
            />
          </Box>
        }
        w={257}
        miw={257}
        title={title}
        data={fundraising?.investorSelect}
        value={fundraisingSelect.investor}
        onChange={value => {
          onFundraisingSelectChange('investor', value)
          setFundraisingSelect(params => ({
            ...params,
            investor: value,
          }))
        }}
        extra={
          isMore ? (
            <Flex h={40} w={'100%'} justify="center" pb={8}>
              <Button
                loading={loading}
                styles={{
                  root: {
                    [ButtonRootVariable.BUTTON_COLOR]: 'var(--color-text-5)',
                    [ButtonRootVariable.BUTTON_HOVER_COLOR]: 'var(--color-brand-2)',
                  },
                }}
                type="text"
                label={t('p.more', { ns: 'fundraising' }) + ''}
                onClick={() => {
                  runAsync({
                    lang: locale,
                    page: (fundraising?.investorSelect?.page ?? 1) + 1,
                    pageSize: 20,
                    name: search,
                  }).then(res => {
                    setInvestor({
                      ...res,
                      list: fundraising?.investorSelect?.list?.concat(res?.list),
                    })
                  })
                }}
              />
            </Flex>
          ) : undefined
        }
      />
    </Flex>
  )
}

export default memo(observer(FundraisingInvestors))
